import {
    AlertModal,
    Box,
    Button,
    Divider,
    Edit,
    Heading,
    IconButton,
    showToast,
    TextBlock,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import { Field, Form, Formik, FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";

import styles from "components/asset-management-system/custom-views/asset-custom-view-settings-tab.scss";
import { subpageContext } from "components/layout/ApplicationLayout";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import { AUTH_ASSET_MANIFEST_EDIT, AUTH_CUSTOM_ASSET_VIEW_EDIT } from "domain/authority";
import { assetViewService, Manifest } from "services/asset-management/AssetCustomViewService";
import { userSessionService } from "services/user/UserSessionService";
import formStyle from "styles/form.scss";
import { logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    customViewUuid: string;
    viewName?: string;
    onCancel?: () => void;
    readOnly?: boolean;
    onManifestUpdated?: () => void;
    manifest?: Manifest;
    onManifestRefresh?: () => void;
}

export interface ManifestFormValues {
    description: string;
    erasureMethod: string;
    erasureNotes: string;
    organizationName: string;
    location: string;
    address: string;
    primarySiteContact: string;
    primarySiteNumber: string;
    primarySiteEmail: string;
    alternativeSiteContact: string;
    alternativeSiteNumber: string;
    alternativeSiteEmail: string;
    organizationId: string;
    dispositionInstructions: string;
    manifestNotes: string;
    pickupRequestDate: string;
    assetCount: string;
    weight: string;
    transporterCompany: string;
    containerType: string;
    containerId: string;
}

const EMPTY_VALUE = "-";

const displayValue = (value: string | undefined): string => value ?? EMPTY_VALUE;

const buildFormValues = (manifest: Manifest | undefined): ManifestFormValues => ({
    description: manifest?.description ?? "",
    erasureMethod: manifest?.erasureMethod ?? "",
    erasureNotes: manifest?.erasureNotes ?? "",
    organizationName: manifest?.organizationName ?? "",
    location: manifest?.location ?? "",
    address: manifest?.address ?? "",
    primarySiteContact: manifest?.primarySiteContact ?? "",
    primarySiteNumber: manifest?.primarySiteNumber ?? "",
    primarySiteEmail: manifest?.primarySiteEmail ?? "",
    alternativeSiteContact: manifest?.alternativeSiteContact ?? "",
    alternativeSiteNumber: manifest?.alternativeSiteNumber ?? "",
    alternativeSiteEmail: manifest?.alternativeSiteEmail ?? "",
    organizationId: manifest?.organizationId ?? "",
    dispositionInstructions: manifest?.dispositionInstructions ?? "",
    manifestNotes: manifest?.manifestNotes ?? "",
    pickupRequestDate: manifest?.pickupRequestDate ?? "",
    assetCount: manifest?.assetCount ?? "",
    weight: manifest?.weight ?? "",
    transporterCompany: manifest?.transporterCompany ?? "",
    containerType: manifest?.containerType ?? "",
    containerId: manifest?.containerId ?? "",
});

const ManifestHeader = ({ title, onEditClick }: { title: string; onEditClick?: () => void }): JSX.Element => {
    const theme = useTheme();
    return (
        <Box verticalAlignment="Center" gap="Xs">
            <Heading variant="h2">{title}</Heading>
            {onEditClick && (
                <Box verticalAlignment="Center" gap="Xs">
                    <IconButton onClick={onEditClick}>
                        <Edit color={theme.iconFillColor} />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
};

export const ManifestTab = (props: Props) => {
    const { t } = useTranslation();
    const context = React.useContext(subpageContext);
    const [isErrorModalOpen, setIsErrorModalOpen] = React.useState(false);

    const hasEditAuthority =
        props.readOnly !== true &&
        userSessionService.userHasAllAuthorities([AUTH_CUSTOM_ASSET_VIEW_EDIT, AUTH_ASSET_MANIFEST_EDIT]);

    const canEditManifest = userSessionService.userHasAllAuthorities([
        AUTH_CUSTOM_ASSET_VIEW_EDIT,
        AUTH_ASSET_MANIFEST_EDIT,
    ]);

    const openManifestEditor = () => {
        context?.setSubpage({
            content: (
                <ManifestTab
                    customViewUuid={props.customViewUuid}
                    viewName={props.viewName}
                    readOnly={false}
                    manifest={props.manifest}
                    onManifestUpdated={() => {
                        props.onManifestRefresh?.();
                        props.onManifestUpdated?.();
                    }}
                    onCancel={() => context?.setSubpage({ content: undefined })}
                />
            ),
            isModal: true,
        });
    };

    if (hasEditAuthority) {
        const handleSubmit = async (values: ManifestFormValues) => {
            try {
                await assetViewService.updateManifest(props.customViewUuid, {
                    description: values.description || undefined,
                    erasure_method: values.erasureMethod || undefined,
                    erasure_notes: values.erasureNotes || undefined,
                    organization_name: values.organizationName || undefined,
                    location: values.location || undefined,
                    address: values.address || undefined,
                    primary_site_contact: values.primarySiteContact || undefined,
                    primary_site_number: values.primarySiteNumber || undefined,
                    primary_site_email: values.primarySiteEmail || undefined,
                    alternative_site_contact: values.alternativeSiteContact || undefined,
                    alternative_site_number: values.alternativeSiteNumber || undefined,
                    alternative_site_email: values.alternativeSiteEmail || undefined,
                    organization_id: values.organizationId || undefined,
                    disposition_instructions: values.dispositionInstructions || undefined,
                    manifest_notes: values.manifestNotes || undefined,
                    pickup_request_date: values.pickupRequestDate || undefined,
                    asset_count: values.assetCount || undefined,
                    weight: values.weight || undefined,
                    transporter_company: values.transporterCompany || undefined,
                    container_type: values.containerType || undefined,
                    container_id: values.containerId || undefined,
                });
                showToast(t("Common.resultSuccessMessage"), "success");
                props.onManifestUpdated?.();
                if (props.readOnly !== true) {
                    context?.setSubpage({ content: undefined });
                }
            } catch (error) {
                logger.error("Failed to update manifest:", error);
                setIsErrorModalOpen(true);
            }
        };

        const field = (name: keyof ManifestFormValues, labelKey: string) => (
            <Box marginTop="Sm">
                <label className={styles.label} htmlFor={name}>
                    {t(labelKey)}
                </label>
                <Field
                    id={name}
                    name={name}
                    type="text"
                    className={classNames(formStyle.input, formStyle.fixedWidthInput)}
                />
            </Box>
        );

        return (
            <Formik initialValues={buildFormValues(props.manifest)} enableReinitialize onSubmit={handleSubmit}>
                {({ isSubmitting }: FormikProps<ManifestFormValues>) => (
                    <Form id="manifestEditForm">
                        <SubpageLayout
                            visible={true}
                            title={t("AssetManagement.manifest.editManifestDetails")}
                            buttons={
                                <Button variant="Primary" type="submit" form="manifestEditForm" disabled={isSubmitting}>
                                    {t("Common.save")}
                                </Button>
                            }
                            onClose={props.onCancel}
                        >
                            <Box columnLayout>
                                <ManifestHeader title={t("AssetManagement.manifest.manifest")} />
                                {field("description", "AssetManagement.manifest.description")}

                                <Box columnLayout marginTop="Lg">
                                    <Heading variant="h2">{t("AssetManagement.manifest.policyRequirements")}</Heading>
                                    <Box columnLayout marginTop="Sm">
                                        <Heading variant="SUBTITLE_2">
                                            {t("AssetManagement.manifest.erasureRequirements")}
                                        </Heading>
                                        <Divider direction="Horizontal" />
                                        {field("erasureMethod", "AssetManagement.manifest.erasureMethod")}
                                        {field("erasureNotes", "AssetManagement.manifest.erasureNotes")}
                                    </Box>
                                </Box>

                                <Box columnLayout marginTop="Lg">
                                    <Heading variant="h2">{t("AssetManagement.manifest.manifestDetails")}</Heading>
                                    <Box columnLayout marginTop="Sm">
                                        <Heading variant="SUBTITLE_2">
                                            {t("AssetManagement.manifest.organizationSendingAssetInfo")}
                                        </Heading>
                                        <Divider direction="Horizontal" />
                                        {field("organizationName", "AssetManagement.manifest.organizationName")}
                                        {field("location", "AssetManagement.manifest.sitePickupLocation")}
                                        {field("address", "AssetManagement.manifest.address")}
                                        {field("primarySiteContact", "AssetManagement.manifest.primarySiteContact")}
                                        {field("primarySiteNumber", "AssetManagement.manifest.primarySiteNumber")}
                                        {field("primarySiteEmail", "AssetManagement.manifest.primarySiteEmail")}
                                        {field(
                                            "alternativeSiteContact",
                                            "AssetManagement.manifest.alternativeSiteContact"
                                        )}
                                        {field(
                                            "alternativeSiteNumber",
                                            "AssetManagement.manifest.alternativeSiteNumber"
                                        )}
                                        {field("alternativeSiteEmail", "AssetManagement.manifest.alternativeSiteEmail")}
                                        {field("organizationId", "AssetManagement.manifest.organizationId")}
                                        {field(
                                            "dispositionInstructions",
                                            "AssetManagement.manifest.dispositionInstructions"
                                        )}
                                        {field("manifestNotes", "AssetManagement.manifest.manifestNotes")}
                                    </Box>
                                </Box>

                                <Box columnLayout marginTop="Lg">
                                    <Heading variant="h2">{t("AssetManagement.manifest.packagingDetails")}</Heading>
                                    <Box columnLayout marginTop="Sm">
                                        <Heading variant="SUBTITLE_2">
                                            {t("AssetManagement.manifest.pickupLogisticsTitle")}
                                        </Heading>
                                        <Divider direction="Horizontal" />
                                        {field("pickupRequestDate", "AssetManagement.manifest.pickupRequestDate")}
                                        {field("assetCount", "AssetManagement.manifest.assetCount")}
                                        {field("weight", "AssetManagement.manifest.weight")}
                                        {field("transporterCompany", "AssetManagement.manifest.transporterCompany")}
                                        {field("containerType", "AssetManagement.manifest.containerType")}
                                        {field("containerId", "AssetManagement.manifest.containerId")}
                                    </Box>
                                </Box>
                            </Box>
                        </SubpageLayout>
                        <AlertModal
                            title={t("Common.failure")}
                            variant={"Error"}
                            isOpen={isErrorModalOpen}
                            hideModal={() => setIsErrorModalOpen(false)}
                            buttons={
                                <Button onClick={() => setIsErrorModalOpen(false)} variant={"Primary"}>
                                    {t("Common.ok")}
                                </Button>
                            }
                        >
                            <TextBlock>{t("AssetManagement.manifest.updateFailureMessage")}</TextBlock>
                        </AlertModal>
                    </Form>
                )}
            </Formik>
        );
    }

    return (
        <Box columnLayout testId={testIds.workArea.asset.customView.manifest.itself}>
            <Box columnLayout>
                <ManifestHeader
                    title={t("AssetManagement.manifest.manifest")}
                    onEditClick={canEditManifest ? openManifestEditor : undefined}
                />
                <Box marginTop="Sm">
                    <span className={styles.label}>{t("AssetManagement.manifest.description")}</span>
                    <span>{displayValue(props.manifest?.description)}</span>
                </Box>
            </Box>

            <Box columnLayout marginTop="Lg">
                <Heading variant="h2">{t("AssetManagement.manifest.policyRequirements")}</Heading>
                <Box columnLayout marginTop="Sm">
                    <Heading variant="SUBTITLE_2">{t("AssetManagement.manifest.erasureRequirements")}</Heading>
                    <Divider direction="Horizontal" />
                    <Box marginTop="Sm">
                        <span className={styles.label}>{t("AssetManagement.manifest.erasureMethod")}</span>
                        <span>{displayValue(props.manifest?.erasureMethod)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.erasureNotes")}</span>
                        <span>{displayValue(props.manifest?.erasureNotes)}</span>
                    </Box>
                </Box>
            </Box>

            <Box columnLayout marginTop="Lg">
                <Heading variant="h2">{t("AssetManagement.manifest.manifestDetails")}</Heading>
                <Box columnLayout marginTop="Sm">
                    <Heading variant="SUBTITLE_2">{t("AssetManagement.manifest.organizationSendingAssetInfo")}</Heading>
                    <Divider direction="Horizontal" />
                    <Box marginTop="Sm">
                        <span className={styles.label}>{t("AssetManagement.manifest.organizationName")}</span>
                        <span>{displayValue(props.manifest?.organizationName)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.sitePickupLocation")}</span>
                        <span>{displayValue(props.manifest?.location)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.address")}</span>
                        <span>{displayValue(props.manifest?.address)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.primarySiteContact")}</span>
                        <span>{displayValue(props.manifest?.primarySiteContact)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.primarySiteNumber")}</span>
                        <span>{displayValue(props.manifest?.primarySiteNumber)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.primarySiteEmail")}</span>
                        <span>{displayValue(props.manifest?.primarySiteEmail)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.alternativeSiteContact")}</span>
                        <span>{displayValue(props.manifest?.alternativeSiteContact)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.alternativeSiteNumber")}</span>
                        <span>{displayValue(props.manifest?.alternativeSiteNumber)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.alternativeSiteEmail")}</span>
                        <span>{displayValue(props.manifest?.alternativeSiteEmail)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.organizationId")}</span>
                        <span>{displayValue(props.manifest?.organizationId)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.dispositionInstructions")}</span>
                        <span>{displayValue(props.manifest?.dispositionInstructions)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.manifestNotes")}</span>
                        <span>{displayValue(props.manifest?.manifestNotes)}</span>
                    </Box>
                </Box>
            </Box>

            <Box columnLayout marginTop="Lg">
                <Heading variant="h2">{t("AssetManagement.manifest.packagingDetails")}</Heading>
                <Box columnLayout marginTop="Sm">
                    <Heading variant="SUBTITLE_2">{t("AssetManagement.manifest.pickupLogisticsTitle")}</Heading>
                    <Divider direction="Horizontal" />
                    <Box marginTop="Sm">
                        <span className={styles.label}>{t("AssetManagement.manifest.pickupRequestDate")}</span>
                        <span>{displayValue(props.manifest?.pickupRequestDate)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.assetCount")}</span>
                        <span>{displayValue(props.manifest?.assetCount)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.weight")}</span>
                        <span>{displayValue(props.manifest?.weight)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.transporterCompany")}</span>
                        <span>{displayValue(props.manifest?.transporterCompany)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.containerType")}</span>
                        <span>{displayValue(props.manifest?.containerType)}</span>
                    </Box>
                    <Box marginTop="Xs">
                        <span className={styles.label}>{t("AssetManagement.manifest.containerId")}</span>
                        <span>{displayValue(props.manifest?.containerId)}</span>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
